<template>
    <v-dialog
        v-model="showDialog"
        width="500"
        scrollable
        :fullscreen="this.$vuetify.breakpoint.xsOnly"
    >
        <v-card id="assign-dialog">
            <v-card-title>
                <v-icon
                    left
                    color="primary"
                    class="material-icons-outlined action-icon"
                >
                    assignment_turned_in
                </v-icon>
                Assign
            </v-card-title>
            <v-divider />
            <v-card-text 
                ref="cardText"
                class="pt-3"
            >
                <v-form
                    ref="form"
                    v-model="formValid"
                >
                    <div class="assign-info-box mb-5">
                        {{ headerText }}
                    </div>
                    <v-data-table
                        v-if="sameTicketMultiSelected"
                        v-model="selectedMembers"
                        :headers="membersTableHeaders"
                        :items="members"
                        item-key="member"
                        show-select
                        hide-default-footer
                        class="mt-2 mb-6"
                        mobile-breakpoint="0"
                    >
                        <template 
                            v-if="singleTicketSelected"
                            v-slot:[`header.data-table-select`]
                        >
                            <v-simple-checkbox
                                :disabled="singleTicketSelected"
                                :value="singleTicketSelected"
                            />
                        </template>
                        <template 
                            v-if="singleTicketSelected"
                            v-slot:[`item.data-table-select`]
                        >
                            <v-simple-checkbox
                                :disabled="singleTicketSelected"
                                :value="singleTicketSelected"
                            />
                        </template>
                        <template v-slot:[`item.member`]="{item}">
                            {{ item.member }} ({{ item.tickets.length }})
                        </template>
                        <template v-slot:[`item.currentAssignment`]="{ item }">
                            {{ currentAssignment(item) }}
                        </template>
                    </v-data-table>
                    
                    <v-autocomplete
                        v-model="selectedLocator"
                        :items="locators"
                        item-text="text"
                        item-value="value"
                        outlined
                        label="Locator"
                        :rules="[v => (!!v || 'Please select a locator.')]"
                        @change="changeLocator"
                    />
                    <p class="text--secondary">
                        {{ locatorWarningMessage }}
                    </p>
                </v-form>
            </v-card-text>
            <v-divider />
            <v-card-actions>
                <v-spacer />
                <v-btn
                    style="text-transform: none !important"
                    class="text-subtitle-2"
                    outlined
                    :width="this.$vuetify.breakpoint.xs ? '140px' : '76px'"
                    @click="showDialog=false"
                >
                    Cancel
                </v-btn>
                <v-btn
                    style="text-transform: none !important"
                    class="text-subtitle-2"
                    :disabled="disableAssignButton"
                    depressed
                    color="primary"
                    :width="this.$vuetify.breakpoint.xs ? '140px' : '76px'"
                    @click="assign"
                >
                    Assign
                </v-btn>
                <v-spacer v-if="this.$vuetify.breakpoint.xs" />
            </v-card-actions>
        </v-card>
    </v-dialog>
</template>
<script>
import { getCenterId } from '../services/center';
import { hasPermission } from '../utils/permissions';
import webtmsBackend from '@/services/webtmsBackend';
import { guidEqual, guidNormal } from '@/utils/guid.js';
import { isTruthy } from '@/utils/isTruthy.js';

export default {
    props: {
        selectedTickets: {
            type: Array,
            default: () => ([]),
            required: true,
        },
        show: {
            type: Boolean,
            default: false,
        },
        selectAllMembers: {
            type: Boolean,
            default: false,
        }
    },
    data: () => ({
        formValid: false,
        selectedMembers: [],
        showSelectMembersDialog: false,
        selectMembersDialogText: '',
        selectedLocator: '',
        locatorWarningMessage: ''
    }),
    computed:{
        headerText() {
            if(this.selectedTickets.length) {
                if(this.sameTicketMultiSelected) {
                    const ticket = this.firstSelectedTicket;
                    const ticketRevision = ticket.revision ? (ticket.ticket + '-' + ticket.revision) : ticket.ticket;
                    return `Ticket: ${ticketRevision} (${ticket.center}/${ticket.state})`;
                } else {
                    return `${ this.selectedTickets.length } Tickets Selected`;
                }
            }
            return '';
        },
        disableAssignButton() {
            return !(this.formValid && this.selectedMembers.length);
        },
        showDialog: {
            get() {
                return this.show;                
            },
            set() {
                this.$emit('close-assign-dialog');
            },
        },
        members(){
            let keys = [];
            return this.selectedTickets.reduce(function(rv, x) {
                let index = keys.indexOf(x.TR_member_code);
                if(index === -1) {
                    rv.push ({
                        member: x.TR_member_code,
                        tickets:[x.TR_locator_account],
                    });
                    keys.push(x.TR_member_code);
                } else {
                    rv[index].tickets.push(x.TR_locator_account);
                }
                return rv;
            }, []);
        },
        sameTicketMultiSelected() {
            for(let ticket of this.selectedTickets) {
                if(!guidEqual(this.firstSelectedTicket.tid, ticket.tid)) {
                    return false;
                }
            }
            return true;
        },
        singleTicketSelected() {
            return this.selectedTickets.length===1;
        },
        membersTableHeaders(){
            let membersTableHeaders = [
                {
                    text: 'Members',
                    value: 'member'
                },
            ];
            if(this.showCurrentAssignment) {
                membersTableHeaders.push({
                    text: 'Current Assignment',
                    value: 'currentAssignment'
                });
            }
            return membersTableHeaders;
        },
        firstSelectedTicket() {
            return this.selectedTickets[0];
        },
        locators() {
            let locators = [
                {
                    text: 'Select',
                    value: '',                
                },
                {
                    text: 'Un-Assigned',
                    value: '{00000000-0000-0000-0000-000000000000}',                
                },
            ];
            for(let locator of this.$store.getters.companyUsers) {
                if(locator.ingroup === 'Locator') {
                    locators.push({
                        text: `${locator.account} : ${locator.name}`,
                        value: locator.uid,
                    });
                }
            }
            return locators;
        },
        showCurrentAssignment() {
            for(let member of this.members){
                if(member.tickets.length > 1) {
                    return false;
                }
            }
            return true;
        },
        centerId() {
            if(this.selectedTickets.length) {
                return getCenterId(this.firstSelectedTicket.center);
            }
            return undefined;
        },
        state() {
            if(this.selectedTickets.length) {
                return this.firstSelectedTicket.state;
            }
            return undefined;
        },
        hasExcavatorResponse() {
            return hasPermission('EXCAVATOR_RESPONSE');
        }
    },
    watch: {
        showDialog(show) {
            if(show) {
                this.selectedLocator = '';
                this.setControlDefaultValues();
                this.$nextTick(async () => {
                    console.log('getting locatorVacation');
                    const data = await webtmsBackend.getLocatorVacation();
                    if(data && data.locatorVacations) {
                        this.locatorVacation = data.locatorVacations;
                    }
                });
            } else {
                this.$refs.cardText.scrollTop = 0;
                this.clearAllFields();
                this.$refs.form.resetValidation();
            }
        }
    },
    methods: {
        async assign() {
            let postBody = {
                locator: this.selectedLocator,
                tickets: [],
            };
            for(let ticket of this.selectedTickets) {
                const found = this.selectedMembers.find(member => member.member === ticket.TR_member_code);
                if(found) {
                    postBody.tickets.push({
                        tid: ticket.tid,
                        member_code: ticket.TR_member_code,
                    });
                }
            }
            let result = await webtmsBackend.postAssignTicket(postBody);
            if(!result.errors) {
                // raise event for parent to indicate success
                this.$emit('assign-success', result);
            }
        },
        changeLocator() {
            let locator_uid = guidNormal(this.selectedLocator);
            if(this.locatorVacation) {
                let locSettings = this.locatorVacation.filter(op => guidNormal(op.uid) === locator_uid);
                let locSettingsObj = {
                    account: '',
                    name: '',
                    ASSIGNMENT_ON_HOLD: false,
                    openTickets: 0,
                    ALTERNATE_LOCATOR: '',
                    ASSIGNED_TICKET_LIMIT: 0,
                    vacation: [],
                    vacationFuture: []
                };

                const days = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

                for(let set of locSettings) {
                    switch(set.option) {
                        case 'ALTERNATE_LOCATOR':
                            locSettingsObj.ALTERNATE_LOCATOR = set.value;
                            locSettingsObj.account = set.account;  // { account, name, openTickets } are on all settings
                            locSettingsObj.name = set.name;
                            locSettingsObj.openTickets = set.openTickets;
                            break;
                        case 'ASSIGNED_TICKET_LIMIT':
                            locSettingsObj.ASSIGNED_TICKET_LIMIT = Number(set.value);
                            break;
                        case 'ASSIGNMENT_ON_HOLD':
                            locSettingsObj.ASSIGNMENT_ON_HOLD = isTruthy(set.value);
                            break;
                        case 'VACATION1':
                        case 'VACATION2':
                        case 'VACATION3':
                        case 'VACATION4':
                        case 'VACATION5':
                            // handle vacation setting, set vacation = true and vacationDates range
                            // if vacation is in the future, add to futureVacationDates array (of string)
                            if(set.value.length && set.value.indexOf('|') > 0) {
                                let v = set.value.split('|');
                                if(v.length === 2) {
                                    let vd = [ new Date(Date.parse(v[0])), new Date(Date.parse(v[1])) ];
                                    const now = new Date();
                                    if(vd[0] <= now && now <= vd[1]) {
                                        const dayFrom = days[vd[0].getDay()];
                                        const dayTo = days[vd[1].getDay()];
                                        const vdinfo = `Currently on Vacation from ${dayFrom} ${vd[0].toLocaleString()} thru ${dayTo} ${vd[1].toLocaleString()}.`;
                                        locSettingsObj.vacation.push(vdinfo);
                                    } else if(vd[0] > now) {
                                        const dayFrom = days[vd[0].getDay()];
                                        const dayTo = days[vd[1].getDay()];
                                        const vdinfo = `Will be on Vacation from ${dayFrom} ${vd[0].toLocaleString()} thru ${dayTo} ${vd[1].toLocaleString()}.`;
                                        locSettingsObj.vacationFuture.push(vdinfo);
                                    }
                                }
                            }
                            break;
                    }
                }
                if(locSettingsObj.name.length === 0) {
                    this.locatorWarningMessage = '';
                    return;
                }
                this.locatorWarningMessage = `${locSettingsObj.account} "${locSettingsObj.name}" `;
                if(locSettingsObj.ASSIGNMENT_ON_HOLD) {
                    this.locatorWarningMessage += ' is on Auto-Assign HOLD and';
                }
                this.locatorWarningMessage += ` has ${locSettingsObj.openTickets} open tickets`;
                if(locSettingsObj.ASSIGNED_TICKET_LIMIT) {
                    this.locatorWarningMessage += ` and a limit of ${locSettingsObj.ASSIGNED_TICKET_LIMIT} assigned tickets.`;
                } else {
                    this.locatorWarningMessage += '.';
                }
                if(locSettingsObj.vacation.length) {
                    this.locatorWarningMessage += ' ' + locSettingsObj.vacation.join(' ');
                }
                if(locSettingsObj.vacationFuture.length) {
                    this.locatorWarningMessage += ' ' + locSettingsObj.vacationFuture.join(' ');
                }
            } else {
                this.locatorWarningMessage = 'Warning: Locator settings not found or unavailable.';
            }
        },
        async setControlDefaultValues() {
            if(this.singleTicketSelected){
                if( this.members.length) {
                    this.selectedMembers = [this.members[0]];
                }
            } else if(!this.sameTicketMultiSelected
                   || this.selectAllMembers) {
                this.selectedMembers = [...this.members];
            }
        },
        clearAllFields() {
            this.locatorVacation = [];
            this.selectedMembers = [];
        },
        currentAssignment(member) {
            if(this.showCurrentAssignment){
                return member.tickets[0] || 'Un-Assigned';
            }
            return '';
        },
    }
};
</script>
<style>
#assign-dialog .assign-info-box {
    text-align: center;
    height: 40px;
    font-size: 14px;
    background-color: var(--ll-surface-subtle);
    line-height: 40px;
    font-weight: 500;
    color: var(--ll-text-primary);
}
#assign-dialog .v-data-table {
    border: 1px solid var(--ll-border);
    width: 100%;
    overflow: hidden;
}
#assign-dialog.v-data-table__wrapper {
    border-radius: 4px;
}
#assign-dialog thead,
#assign-dialog th {
    background-color: var(--ll-surface-subtle);
}
#locate-date {
    width: 200px;
}
.response-select-item {
    max-width: 410px;
    padding-bottom: 8px;
}
</style>
